// Please do not use optional chaining (?.), nullish coalescing (??), or other ES2020+ features

import React, { useEffect, useState, useRef, memo, useMemo, useCallback } from 'react';
import ReactFlow, {
  Background,
  Controls,
  ControlButton,
} from 'react-flow-renderer';
import 'react-flow-renderer/dist/style.css';
import { getNodeColor } from '../../../../utils';
import ConceptNode from './ConceptNode';
import IndividNode from './IndividNode';
import { styled } from '@mui/material/styles';
import { Box } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import ModeOutlinedIcon from '@mui/icons-material/ModeOutlined';

const defaultColor = '#347CFF'; // default blue color
const extraIndividPadding = 40; // Extra left-right space between individuals and columns

const StyledReactFlowComponent = styled(ReactFlow)({
  width: '100%',
  height: '100%',
  background: '#000000',
  '& .react-flow__node': {
    fontSize: '14px',
    color: '#ffffff'
  },
  '& .react-flow__edge-path': {
    strokeLinecap: 'round',
    strokeLinejoin: 'round'
  },
  '& .react-flow__edge-text': {
    fill: '#ffffff',
    fontSize: '12px',
    fontWeight: 500,
    letterSpacing: '0.5px',
    textTransform: 'uppercase'
  },
  '& .react-flow__edge-textbg': {
    fill: '#000000',
    rx: 4,
    ry: 4
  },
  '& .react-flow__edge-text-bg': {
    fill: '#000000',
    rx: 4,
    ry: 4
  },
  '& .react-flow__handle': {
    width: '6px',
    height: '6px',
    backgroundColor: 'currentColor',
    border: 'none',
    borderRadius: '50%'
  },
  '& .react-flow__controls': {
    background: '#1e293b',
    borderRadius: '8px',
    padding: '4px',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    button: {
      background: '#334155',
      border: 'none',
      borderRadius: '4px',
      color: '#ffffff',
      '&:hover': {
        background: '#475569'
      },
      '&.react-flow__controls-interactive': {
        display: 'none'
      }
    }
  }
});

// Helper: get display title
function getTitle(node) {
  return node.title || node.id;
}

// Helper: get node color with default fallback
function getNodeColorById(node) {
  return node && node.id ? getNodeColor(node.id) : defaultColor;
}

// Helper: estimate node height based on content
function estimateConceptHeight(predicates, showUidData = true) {
  const base = showUidData ? 60 : 40; // base height for title and padding (reduced when UID hidden)
  const perPredicate = 20;
  return base + perPredicate * Object.keys(predicates || {}).length;
}

function estimateIndividHeight(triplets, showUidData = true) {
  const base = showUidData ? 60 : 40; // base height for title and padding (reduced when UID hidden)
  const perTriple = 20;
  return base + perTriple * Object.keys(triplets || {}).length;
}

// Helper: compute AKO levels for each concept
function computeAkoLevels(concepts) {
  // Build a map of concept uid to its parents (ako)
  const parentMap = {};
  concepts.forEach(c => {
    parentMap[c.uid] = (c.ako && c.ako.length > 0) ? c.ako : [];
  });
  // Compute levels (root = 0)
  const levels = {};
  function getLevel(uid) {
    if (levels[uid] !== undefined) return levels[uid];
    const parents = parentMap[uid] || [];
    if (parents.length === 0) {
      levels[uid] = 0;
      return 0;
    }
    // Level is 1 + max(parent levels)
    const parentLevels = parents.map(getLevel);
    const level = 1 + Math.max.apply(null, parentLevels);
    levels[uid] = level;
    return level;
  }
  concepts.forEach(c => { getLevel(c.uid); });
  return levels;
}

// Helper: apply layout algorithm while preserving user positions
function applyLayoutAlgorithm(nodes, concepts, individs, userModifiedPositions, settings) {
  // Layout constants
  const conceptY = 1000; // Y position for all concepts (bottom)
  const individSpacingY = 60; // 60px between individs (increased for visibility)
  const individConceptGap = 30; // 100px from top of concept to bottom of closest individ
  const minConceptPadding = 100; // Minimum padding between columns
  const minColumnWidth = 200; // Fixed width for a column
  const akoVerticalGap = 80; // Extra vertical gap per AKO level

  // Compute AKO levels for concepts
  const akoLevels = computeAkoLevels(concepts);

  // --- Identify AKO target concepts ---
  const akoTargetSet = new Set();
  concepts.forEach(c => {
    (c.ako || []).forEach(parent => {
      akoTargetSet.add(parent);
    });
  });

  // 1. For each concept, collect its individs (ISA)
  const individsByIsa = {};
  individs.forEach(individ => {
    (individ.isa || []).forEach(isa => {
      if (!individsByIsa[isa]) individsByIsa[isa] = [];
      individsByIsa[isa].push(individ);
    });
  });
  // Sort each individsByIsa list by UID for deterministic stacking
  Object.keys(individsByIsa).forEach(isa => {
    individsByIsa[isa].sort((a, b) => String(a.uid).localeCompare(String(b.uid)));
  });

  // 2. Build a map of concept pairs that share individuals (multi-ISA)
  //    and determine the widest shared individual for each pair
  const sharedIndividWidths = {};
  function getIndividWidth(individ) {
    // Estimate width based on content; fallback to a minimum
    return 160 + 10 * (Object.keys((individ.triplets || {})).length) + extraIndividPadding;
  }
  individs.forEach(individ => {
    if (individ.isa && individ.isa.length > 1) {
      for (let i = 0; i < individ.isa.length - 1; i++) {
        for (let j = i + 1; j < individ.isa.length; j++) {
          const a = individ.isa[i];
          const b = individ.isa[j];
          const key = a < b ? a + '|' + b : b + '|' + a;
          const width = getIndividWidth(individ);
          if (!sharedIndividWidths[key] || sharedIndividWidths[key] < width) {
            sharedIndividWidths[key] = width;
          }
        }
      }
    }
  });

  // 3. Calculate X positions for each concept, dynamically increasing spacing for shared individs
  let xCursor = 200; // Start X
  const conceptXMap = {};
  for (let i = 0; i < concepts.length; i++) {
    const concept = concepts[i];
    if (i > 0) {
      // Check if previous concept shares an individ with this one
      const prevConcept = concepts[i - 1];
      const key = prevConcept.uid < concept.uid ? prevConcept.uid + '|' + concept.uid : concept.uid + '|' + prevConcept.uid;
      let extra = 0;
      if (sharedIndividWidths[key]) {
        // Add enough space to fit the widest shared individ
        extra = Math.max(0, sharedIndividWidths[key] - minConceptPadding);
      }
      xCursor += minColumnWidth + minConceptPadding + extra;
    }
    conceptXMap[concept.uid] = xCursor + minColumnWidth / 2;
  }

  // 4. For each individ, determine its position
  const individPositions = {};
  const conceptStackCount = {};
  concepts.forEach(c => { conceptStackCount[c.uid] = 0; });

  function getIndividHeight(individ) {
    return estimateIndividHeight(individ.triplets || {}, settings.showUidData);
  }
  function getConceptHeight(concept) {
    return estimateConceptHeight(concept.predicates || {}, settings.showUidData);
  }

  // Single-ISA individs (stack upward above concept, no overlap, 20px spacing)
  Object.keys(individsByIsa).forEach(isa => {
    const stack = individsByIsa[isa];
    const x = conceptXMap[isa] || 0;
    const concept = concepts.find(c => c.uid === isa) || {};
    const conceptHeight = getConceptHeight(concept);
    const baseY = conceptY - conceptHeight - individConceptGap;
    let yCursor = baseY;
    // Stack upward: first individ is just above the concept, next is above the previous, etc.
    for (let i = 0; i < stack.length; i++) {
      const individ = stack[i];
      if (userModifiedPositions.get(individ.uid)) continue;
      const individHeight = getIndividHeight(individ);
      yCursor -= individHeight;
      individPositions[individ.uid] = { x, y: yCursor };
      yCursor -= individSpacingY; // 20px
    }
  });

  // Multi-ISA individs (grouped by ISA set)
  const multiIsaGroups = {};
  individs.forEach(individ => {
    if (userModifiedPositions.get(individ.uid)) return;
    if (!individ.isa || individ.isa.length < 2) return;
    const key = individ.isa.slice().sort().join('|');
    if (!multiIsaGroups[key]) multiIsaGroups[key] = [];
    multiIsaGroups[key].push(individ);
  });

  // Multi-ISA individs (center above all parent concepts, stack upward, 20px spacing)
  Object.keys(multiIsaGroups).forEach(key => {
    const group = multiIsaGroups[key];
    // Get all parent concept Xs
    const isaArr = key.split('|');
    const xs = isaArr.map(isa => conceptXMap[isa] || 0);
    const minX = Math.min.apply(null, xs);
    const maxX = Math.max.apply(null, xs);
    const x = (minX + maxX) / 2;
    // Use the minimum Y base among all parent concepts
    const conceptsArr = isaArr.map(isa => concepts.find(c => c.uid === isa) || {});
    const conceptHeights = conceptsArr.map(getConceptHeight);
    const minConceptHeight = Math.min.apply(null, conceptHeights);
    const minLevel = Math.min.apply(null, isaArr.map(isa => akoLevels[isa] || 0));
    const baseY = conceptY - minConceptHeight - individConceptGap - minLevel * akoVerticalGap;
    let yCursor = baseY;
    for (let i = 0; i < group.length; i++) {
      const individ = group[i];
      if (userModifiedPositions.get(individ.uid)) continue;
      const individHeight = getIndividHeight(individ);
      yCursor -= individHeight;
      individPositions[individ.uid] = { x, y: yCursor };
      yCursor -= individSpacingY; // 20px
    }
  });

  // 5. Assign positions to nodes
  let positionedNodes = nodes.map(node => {
    const userPos = userModifiedPositions.get(node.id);
    if (userPos) return { ...node, position: userPos };
    if (conceptXMap[node.id] !== undefined && node.type === 'concept') {
      // If this concept is an AKO target, make it lower by Y
      const akoTargetOffset = akoTargetSet.has(node.id) ? 40 : 0;
      return { ...node, position: { x: conceptXMap[node.id], y: conceptY + akoTargetOffset } };
    }
    if (individPositions[node.id]) {
      return { ...node, position: individPositions[node.id] };
    }
    return node;
  });

  // --- Placeholders: move 'Missing' nodes closer to their references ---
  // Find all placeholder nodes
  const placeholderNodes = positionedNodes.filter(n => n.isPlaceholder);
  if (placeholderNodes.length > 0) {
    // Build a map of node positions for lookup
    const nodePosMap = {};
    positionedNodes.forEach(n => { nodePosMap[n.id] = n.position; });
  }
  return positionedNodes;
}

// Helper: get a unique key for a node (uid if present, else id, else rawId)
function getNodeKey(node) {
  return node && (node.uid || node.id || node.rawId);
}

const OntologyGraph = (({ graph, updateGraph, trackDeletedIds, graphSettings, resetCounter }) => {
  const [elements, setElements] = useState({ nodes: [], edges: [] });
  const [isGraphReady, setIsGraphReady] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [selectedNodeIds, setSelectedNodeIds] = useState([]);
  const [deletionMode, setDeletionMode] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [viewport, setViewport] = useState(null);
  const [internalResetCounter, setInternalResetCounter] = useState(0);
  const wrapperRef = useRef(null);
  const previousPositions = useRef(new Map());
  const userModifiedPositions = useRef(new Map());
  const reactFlowInstance = useRef(null);
  const prevGraphDataRef = useRef(null);

  const defaultZoom = 0.7;

  // Default settings if not provided
  const settings = graphSettings || {
    showIsaLabels: true,
    showAkoLabels: true,
    monochromeView: false,
    showUidData: true,
  };

  // Track when settings change to clear positions if needed
  const prevSettingsRef = useRef(settings);
  
  // Only clear positions when it's a fresh load (not when settings change)
  useEffect(() => {
    const prevSettings = prevSettingsRef.current;
    // Only clear positions if this is a fresh load (no previous settings)
    if (!prevSettings) {
      userModifiedPositions.current.clear();
      previousPositions.current.clear();
    }
    prevSettingsRef.current = settings;
  }, [settings]);

  // Force re-render when settings change
  useEffect(() => {
    if (graph && elements.nodes.length > 0) {
      // Trigger a re-render by updating elements
      setElements(prevElements => ({ ...prevElements }));
    }
  }, [settings, graph]);

  // Handle reset trigger from parent
  useEffect(() => {
    if (resetCounter > internalResetCounter) {
      setInternalResetCounter(resetCounter);
      // Clear all positions and force complete re-render
      userModifiedPositions.current.clear();
      previousPositions.current.clear();
      setElements({ nodes: [], edges: [] });
      setIsGraphReady(false);
    }
  }, [resetCounter, internalResetCounter]);

  // Track viewport changes
  const onMoveEnd = useCallback((event, viewport) => {
    setViewport(viewport);
  }, []);

  const onLoad = useCallback((instance) => {
    reactFlowInstance.current = instance;
    // Only fit view if there's no saved viewport
    if (!viewport) {
      setTimeout(() => {
        instance.fitView({ padding: 0.2, includeHiddenNodes: true });
      }, 0);
    }
  }, [viewport]);

  // Callback to track node position changes when user drags nodes
  const onNodeDragStop = useCallback((event, node) => {
    // Save the dragged node's position
    userModifiedPositions.current.set(node.id, { x: node.position.x, y: node.position.y });

    // If it's an individual, lock all individuals in the same column
    if (node.type === 'individ' && graph && Array.isArray(graph.individuals)) {
      // Find the individ in the graph data
      const individ = graph.individuals.find(i => String(i.uid) === String(node.id));
      if (individ && Array.isArray(individ.isa) && individ.isa.length > 0) {
        // For each parent concept (isa), find all individuals with the same isa
        individ.isa.forEach(isaId => {
          graph.individuals.forEach(otherIndivid => {
            if (
              Array.isArray(otherIndivid.isa) &&
              otherIndivid.isa.includes(isaId)
            ) {
              // Find the node in the current elements
              const otherNode = elements.nodes.find(n => String(n.id) === String(otherIndivid.uid));
              if (otherNode) {
                userModifiedPositions.current.set(otherNode.id, { x: otherNode.position.x, y: otherNode.position.y });
              }
            }
          });
        });
      }
    }
  }, [graph, elements.nodes]);

  useEffect(() => {
    if (!graph) {
      setIsGraphReady(false);
      return;
    }
    
    // Check if graph has the new structure with concepts and individuals
    if (!graph.concepts && !graph.individuals) {
      setIsGraphReady(false);
      return;
    }
    
    const concepts = graph.concepts || [];
    const individuals = graph.individuals || [];
    
    if (concepts.length === 0 && individuals.length === 0) {
      setIsGraphReady(false);
      return;
    }
    
    // Check if only settings changed (not graph data)
    const currentGraphData = JSON.stringify({ concepts, individuals });
    const prevGraphData = prevGraphDataRef.current;
    const onlySettingsChanged = prevGraphData && prevGraphData === currentGraphData;
    
    // If only settings changed, preserve existing positions and just update node data
    if (onlySettingsChanged && elements.nodes.length > 0) {
      const updatedNodes = elements.nodes.map(node => {
        const nodeData = node.data;
        // Update node data with new settings
        const updatedData = {
          ...nodeData,
          showUidData: settings.showUidData,
          color: settings.monochromeView ? defaultColor : (nodeData.originalColor || nodeData.color)
        };
        
        // Update node height based on new settings
        const newHeight = node.type === 'concept' ? 
          estimateConceptHeight(nodeData.predicates, settings.showUidData) :
          estimateIndividHeight(nodeData.triplets || {}, settings.showUidData);
        
        return {
          ...node,
          data: updatedData,
          nodeHeight: newHeight
        };
      });
      
      // Update edges based on new settings
      const updatedEdges = elements.edges.map(edge => {
        let updatedEdge = { ...edge };
        
        if (edge.id.startsWith('ako-')) {
          // For AKO edges, find the source node to get its original color
          const sourceNode = updatedNodes.find(n => n.id === edge.source);
          const originalColor = sourceNode ? (sourceNode.data.originalColor || sourceNode.data.color) : edge.style.stroke;
          const edgeColor = settings.monochromeView ? defaultColor : originalColor;
          
          updatedEdge = {
            ...updatedEdge,
            label: settings.showAkoLabels ? 'AKO' : '',
            style: {
              ...updatedEdge.style,
              stroke: edgeColor
            },
            labelStyle: {
              ...updatedEdge.labelStyle,
              fill: edgeColor
            }
          };
        } else if (edge.id.startsWith('isa-')) {
          // For ISA edges, use the original blue color or source node color
          const sourceNode = updatedNodes.find(n => n.id === edge.source);
          const originalColor = sourceNode ? (sourceNode.data.originalColor || sourceNode.data.color) : 'rgb(112, 128, 242)';
          const edgeColor = settings.monochromeView ? defaultColor : originalColor;
          
          updatedEdge = {
            ...updatedEdge,
            label: settings.showIsaLabels ? 'ISA' : '',
            style: {
              ...updatedEdge.style,
              stroke: edgeColor
            },
            labelStyle: {
              ...updatedEdge.labelStyle,
              fill: edgeColor
            }
          };
        } else if (edge.id.startsWith('obj-')) {
          // For OBJECT edges, find the source node to get its original color
          const sourceNode = updatedNodes.find(n => n.id === edge.source);
          const originalColor = sourceNode ? (sourceNode.data.originalColor || sourceNode.data.color) : edge.style.stroke;
          const edgeColor = settings.monochromeView ? defaultColor : originalColor;
          
          updatedEdge = {
            ...updatedEdge,
            style: {
              ...updatedEdge.style,
              stroke: edgeColor
            },
            labelStyle: {
              ...updatedEdge.labelStyle,
              fill: edgeColor
            }
          };
        }
        
        return updatedEdge;
      });
      
      setElements({ nodes: updatedNodes, edges: updatedEdges });
      return;
    }
    
    // Update the previous graph data reference
    prevGraphDataRef.current = currentGraphData;

    // Validate each item has required fields
    const validConcepts = concepts.filter(item => {
      if (!item || typeof item !== 'object') {
        return false;
      }
      if (!item.uid) {
        return false;
      }
      return true;
    });
    
    const validIndividuals = individuals.filter(item => {
      if (!item || typeof item !== 'object') {
        return false;
      }
      if (!item.uid) {
        return false;
      }
      return true;
    });
    
    // Create a map of existing nodes to preserve them
    const existingNodes = new Map();
    elements.nodes.forEach(node => {
      existingNodes.set(getNodeKey(node), node);
    });

    // Create ALL nodes - preserve existing ones and add new ones
    const allNodes = [];
    const nodeMap = new Map(); // Map uid to node for easy lookup
    const occupiedPositions = new Set(); // Track all positions being used
    
    // Initialize occupied positions with existing node positions and user-modified positions
    elements.nodes.forEach(node => {
      occupiedPositions.add(`${Math.round(node.position.x)}|${Math.round(node.position.y)}`);
    });
    userModifiedPositions.current.forEach(pos => {
      occupiedPositions.add(`${Math.round(pos.x)}|${Math.round(pos.y)}`);
    });
    
    // Helper function to check if a position overlaps with any existing node
    const isPositionOccupied = (x, y, nodeType, nodeData, excludeUid) => {
      const posKey = `${Math.round(x)}|${Math.round(y)}`;
      if (occupiedPositions.has(posKey)) {
        return true;
      }
      
      // Check for overlap with existing nodes based on estimated node dimensions
      const nodeHeight = nodeType === 'concept' ? 
        estimateConceptHeight(nodeData.predicates, settings.showUidData) : 
        estimateIndividHeight(nodeData.triplets || {}, settings.showUidData);
      const nodeWidth = 200; // Approximate node width
      const safetyMargin = 50; // Extra space between nodes
      
      // Check if this position would overlap with any existing node
      for (const [existingUid, existingNode] of existingNodes) {
        if (existingUid === excludeUid) continue; // Skip the node we're positioning
        if (existingNode.position) {
          const existingHeight = existingNode.nodeHeight || 
            (existingNode.type === 'concept' ? 
              estimateConceptHeight(existingNode.data.predicates, settings.showUidData) : 
              estimateIndividHeight(existingNode.data.triplets || {}, settings.showUidData));
          const existingWidth = 200; // Approximate node width
          
          // Check for overlap with safety margin
          const horizontalOverlap = Math.abs(x - existingNode.position.x) < (nodeWidth + existingWidth) / 2 + safetyMargin;
          const verticalOverlap = Math.abs(y - existingNode.position.y) < (nodeHeight + existingHeight) / 2 + safetyMargin;
          
          if (horizontalOverlap && verticalOverlap) {
            return true;
          }
        }
      }
      
      // Check against nodes that have already been positioned in this iteration
      for (const positionedNode of allNodes) {
        if (positionedNode.id === excludeUid) continue; // Skip the node we're positioning
        if (positionedNode.position) {
          const positionedHeight = positionedNode.nodeHeight || 
            (positionedNode.type === 'concept' ? 
              estimateConceptHeight(positionedNode.data.predicates, settings.showUidData) : 
              estimateIndividHeight(positionedNode.data.triplets || {}, settings.showUidData));
          const positionedWidth = 200; // Approximate node width
          
          // Check for overlap with safety margin
          const horizontalOverlap = Math.abs(x - positionedNode.position.x) < (nodeWidth + positionedWidth) / 2 + safetyMargin;
          const verticalOverlap = Math.abs(y - positionedNode.position.y) < (nodeHeight + positionedHeight) / 2 + safetyMargin;
          
          if (horizontalOverlap && verticalOverlap) {
            return true;
          }
        }
      }
      
      return false;
    };
    
    // Helper function to get position for a node
    // 
    // Sibling column-based positioning logic:
    // - For new concepts: Position above the highest concept in the same AKO column
    // - For new individuals: Position above the highest individual in the same ISA column
    // - Preserves user-modified positions (highest priority)
    // - Preserves existing node positions
    // - Avoids overlaps by adjusting position if collision detected
    // - Falls back to default positioning if no siblings found
    const getNodePosition = (uid, defaultX, defaultY, nodeType, nodeData) => {
      const userPos = userModifiedPositions.current.get(uid);
      
      // If user has modified the position, always use it
      if (userPos) {
        return userPos;
      }
      
      const prevPos = previousPositions.current.get(uid);
      const existingPos = existingNodes.get(uid) ? existingNodes.get(uid).position : { x: defaultX, y: defaultY };
      let pos = prevPos || existingPos || { x: defaultX, y: defaultY };

      // For new nodes (not in existingNodes), apply sibling column-based positioning
      if (!existingNodes.get(uid)) {
        // For concepts, find the highest concept in the same AKO column
        if (nodeType === 'concept') {
          let highestY = -Infinity;
          let referenceX = defaultX;
          
          if (nodeData && nodeData.ako && nodeData.ako.length > 0) {
            // Find all concepts that share the same AKO parents (siblings in the same column)
            validConcepts.forEach(concept => {
              if (concept.uid !== uid && concept.ako && concept.ako.length > 0) {
                // Check if they share any AKO parents (same column)
                const sharedAko = concept.ako.filter(ako => nodeData.ako.includes(ako));
                if (sharedAko.length > 0) {
                  const conceptNode = existingNodes.get(concept.uid);
                  if (conceptNode && conceptNode.position.y > highestY) {
                    highestY = conceptNode.position.y;
                    referenceX = conceptNode.position.x;
                  }
                }
              }
            });
          } else {
            // For concepts without AKO parents, find concepts in the same general area
            validConcepts.forEach(concept => {
              if (concept.uid !== uid) {
                const conceptNode = existingNodes.get(concept.uid);
                if (conceptNode && conceptNode.position.y > highestY) {
                  highestY = conceptNode.position.y;
                  referenceX = conceptNode.position.x;
                }
              }
            });
          }
          
          // If we found a sibling concept, position above it
          if (highestY !== -Infinity) {
            pos = { x: referenceX, y: highestY - 100 }; // 100px above the highest sibling
          } else {
            // No siblings found, position at the top of the graph
            const topY = Math.min(...Array.from(existingNodes.values()).map(n => n.position.y), 100);
            pos = { x: defaultX, y: topY - 100 };
          }
        }
        
        // For individuals, find the highest individual in the same ISA column
        if (nodeType === 'individ') {
          let highestY = -Infinity;
          let referenceX = defaultX;
          
          if (nodeData && nodeData.isa && nodeData.isa.length > 0) {
            // Find all individuals that share the same ISA relationships (siblings in the same column)
            validIndividuals.forEach(individ => {
              if (individ.uid !== uid && individ.isa && individ.isa.length > 0) {
                // Check if they share any ISA relationships (same column)
                const sharedIsa = individ.isa.filter(isa => nodeData.isa.includes(isa));
                if (sharedIsa.length > 0) {
                  const individNode = existingNodes.get(individ.uid);
                  if (individNode && individNode.position.y > highestY) {
                    highestY = individNode.position.y;
                    referenceX = individNode.position.x;
                  }
                }
              }
            });
          } else {
            // For individuals without ISA relationships, find individuals in the same general area
            validIndividuals.forEach(individ => {
              if (individ.uid !== uid) {
                const individNode = existingNodes.get(individ.uid);
                if (individNode && individNode.position.y > highestY) {
                  highestY = individNode.position.y;
                  referenceX = individNode.position.x;
                }
              }
            });
          }
          
          // If we found a sibling individual, position above it
          if (highestY !== -Infinity) {
            pos = { x: referenceX, y: highestY - 80 }; // 80px above the highest sibling
          } else {
            // No siblings found, position at the top of the graph
            const topY = Math.min(...Array.from(existingNodes.values()).map(n => n.position.y), 200);
            pos = { x: defaultX, y: topY - 80 };
          }
        }
        
        // Avoid overlap: check for collisions and adjust if needed
        let tryX = pos.x, tryY = pos.y;
        let tries = 0;
        const maxTries = 100; // Increased max tries for better collision avoidance
        
        while (isPositionOccupied(tryX, tryY, nodeType, nodeData, uid) && tries < maxTries) {
          // Try different patterns to find a free position
          if (tries < 20) {
            // First try: move diagonally
            tryX += 50;
            tryY += 50;
          } else if (tries < 40) {
            // Second try: move horizontally
            tryX += 100;
            tryY = pos.y;
          } else if (tries < 60) {
            // Third try: move vertically
            tryX = pos.x;
            tryY -= 100;
          } else {
            // Fourth try: spiral pattern
            const angle = (tries - 60) * 0.5;
            const radius = 100 + (tries - 60) * 10;
            tryX = pos.x + Math.cos(angle) * radius;
            tryY = pos.y + Math.sin(angle) * radius;
          }
          tries++;
        }
        
        pos = { x: tryX, y: tryY };
        // Mark this position as occupied
        occupiedPositions.add(`${Math.round(tryX)}|${Math.round(tryY)}`);
      }
      return pos;
    };
    
    // Add all concepts first - preserve existing ones
    validConcepts.forEach((concept, index) => {
      const key = getNodeKey(concept);
      const existingNode = existingNodes.get(key);
      const userPos = userModifiedPositions.current.get(key);
      const position = userPos || getNodePosition(key, 200 + (index * 250), 1000, 'concept', concept);
      const nodeId = key;
      // Apply monochrome color if setting is enabled
      const nodeColor = settings.monochromeView ? defaultColor : getNodeColorById(concept);
      // Always create a new data object
      const node = {
        id: key,
        uid: concept.uid,
        rawId: concept.id,
        type: 'concept',
        position: position,
        data: {
          tag: String(concept.uid || concept.id),
          title: getTitle(concept),
          color: nodeColor,
          originalColor: getNodeColorById(concept),
          predicates: concept.predicates,
          onSelectToggle: function() { toggleSelectNode(nodeId); },
          rawId: concept.id,
          showUidData: settings.showUidData
        },
        style: { zIndex: 2 },
        nodeHeight: estimateConceptHeight(concept.predicates, settings.showUidData)
      };
      
      // Preserve any additional properties from existing node, but ensure data is updated
      if (existingNode) {
        // Create a new node that preserves existing properties but updates data
        const updatedNode = { ...existingNode, ...node };
        // Ensure data is always updated with new information
        updatedNode.data = {
          ...existingNode.data,
          ...node.data
        };
        // Keep the calculated position (user position takes priority)
        updatedNode.position = position;
        // Update nodeHeight based on new predicates
        updatedNode.nodeHeight = estimateConceptHeight(concept.predicates, settings.showUidData);
        allNodes.push(updatedNode);
        nodeMap.set(key, updatedNode);
        // Mark this position as occupied
        occupiedPositions.add(`${Math.round(position.x)}|${Math.round(position.y)}`);
      } else {
        allNodes.push(node);
        nodeMap.set(key, node);
        // Mark this position as occupied
        occupiedPositions.add(`${Math.round(position.x)}|${Math.round(position.y)}`);
      }
    });
    
    // Add all individuals - preserve existing ones
    validIndividuals.forEach((individ, index) => {
      const key = getNodeKey(individ);
      const existingNode = existingNodes.get(key);
      const userPos = userModifiedPositions.current.get(key);
      const position = userPos || getNodePosition(key, 200 + (index * 250), 800, 'individ', individ);
      const nodeId = key;
      // Apply monochrome color if setting is enabled
      const nodeColor = settings.monochromeView ? defaultColor : getNodeColorById(individ);
      // Always create a new data object
      const node = {
        id: key,
        uid: individ.uid,
        rawId: individ.id,
        type: 'individ',
        position: position,
        data: {
          tag: String(individ.uid || individ.id),
          title: getTitle(individ),
          color: nodeColor,
          originalColor: getNodeColorById(individ),
          triplets: individ.triplets,
          onSelectToggle: function() { toggleSelectNode(nodeId); },
          rawId: individ.id,
          showUidData: settings.showUidData
        },
        style: { zIndex: 1 },
        nodeHeight: estimateIndividHeight(individ.triplets || {}, settings.showUidData)
      };
      
      // Preserve any additional properties from existing node, but ensure data is updated
      if (existingNode) {
        // Create a new node that preserves existing properties but updates data
        const updatedNode = { ...existingNode, ...node };
        // Ensure data is always updated with new information
        updatedNode.data = {
          ...existingNode.data,
          ...node.data
        };
        // Keep the calculated position (user position takes priority)
        updatedNode.position = position;
        // Update nodeHeight based on new triplets
        updatedNode.nodeHeight = estimateIndividHeight(individ.triplets || {}, settings.showUidData);
        allNodes.push(updatedNode);
        nodeMap.set(key, updatedNode);
        // Mark this position as occupied
        occupiedPositions.add(`${Math.round(position.x)}|${Math.round(position.y)}`);
      } else {
        allNodes.push(node);
        nodeMap.set(key, node);
        // Mark this position as occupied
        occupiedPositions.add(`${Math.round(position.x)}|${Math.round(position.y)}`);
      }
    });
    
    // Add any existing nodes that are not in the new data (preserve them)
    const newUids = new Set([...validConcepts.map(getNodeKey), ...validIndividuals.map(getNodeKey)]);
    let preservedCount = 0;
    
    // Collect all UIDs that are referenced in the new data (for edges, ISA relationships, etc.)
    const referencedUids = new Set();
    
    // Add concept UIDs that are referenced in AKO relationships
    validConcepts.forEach(c => {
      referencedUids.add(getNodeKey(c));
      (c.ako || []).forEach(parent => referencedUids.add(parent));
    });
    
    // Add individ UIDs that are referenced in ISA relationships or OBJECT relationships
    validIndividuals.forEach(ind => {
      referencedUids.add(getNodeKey(ind));
      (ind.isa || []).forEach(isa => referencedUids.add(isa));
      Object.entries(ind.triplets || {}).forEach(([k, v]) => {
        if (v && v.type === 'OBJECT' && v.value) {
          referencedUids.add(v.value);
        }
      });
    });
    
    // Only preserve existing nodes that are referenced in the new data but not explicitly included
    existingNodes.forEach((existingNode, uid) => {
      if (!newUids.has(uid) && !nodeMap.has(uid)) {
        // Check if this existing node is still referenced in the new data
        if (referencedUids.has(uid)) {
          allNodes.push(existingNode);
          nodeMap.set(uid, existingNode);
          // Mark this position as occupied
          occupiedPositions.add(`${Math.round(existingNode.position.x)}|${Math.round(existingNode.position.y)}`);
          preservedCount++;
        } else {
          // Check if user has manually positioned this node (preserve user work)
          const userPos = userModifiedPositions.current.get(uid);
          if (userPos) {
            allNodes.push(existingNode);
            nodeMap.set(uid, existingNode);
            // Mark this position as occupied
            occupiedPositions.add(`${Math.round(existingNode.position.x)}|${Math.round(existingNode.position.y)}`);
            preservedCount++;
          } else {
            // Node is not in new data, not referenced, and not user-modified, so it should be removed
          }
        }
      }
    });
    
    // Debug: Show which existing nodes are being removed
    const removedNodes = [];
    const preservedUserNodes = [];
    existingNodes.forEach((existingNode, uid) => {
      if (!newUids.has(uid) && !referencedUids.has(uid)) {
        const userPos = userModifiedPositions.current.get(uid);
        if (userPos) {
          preservedUserNodes.push({ uid, type: existingNode.type, reason: 'user-modified' });
        } else {
          removedNodes.push({ uid, type: existingNode.type });
        }
      }
    });
    
    // Validate that all nodes have required properties
    const validNodes = allNodes.filter(node => {
      if (!node.id || !node.position || !node.data) {
        return false;
      }
      return true;
    });
    
    // Apply layout algorithm to position nodes that don't have user-modified positions
    const layoutedNodes = applyLayoutAlgorithm(validNodes, validConcepts, validIndividuals, userModifiedPositions.current, settings);
    // Final node processing with preserved positions
    let finalNodes = layoutedNodes.map(node => {
      const userPos = userModifiedPositions.current.get(node.id);
      if (userPos) {
        // Keep user-modified position - this should never be overridden
        return { ...node, position: userPos };
      }
      return node;
    });

    // Double-check: ensure all user-modified positions are preserved
    finalNodes = finalNodes.map(node => {
      const userPos = userModifiedPositions.current.get(node.id);
      if (userPos) {
        return { ...node, position: userPos };
      }
      return node;
    });

    // Apply collision avoidance to prevent overlapping after layout
    const finalOccupiedPositions = new Set();
    finalNodes = finalNodes.map(node => {
      const userPos = userModifiedPositions.current.get(node.id);
      if (userPos) {
        // User positions are never moved for collision avoidance
        finalOccupiedPositions.add(`${Math.round(userPos.x)}|${Math.round(userPos.y)}`);
        return { ...node, position: userPos };
      }
      
      // For non-user-modified nodes, check for collisions
      let pos = node.position;
      let tryX = pos.x, tryY = pos.y;
      let tries = 0;
      while (finalOccupiedPositions.has(`${Math.round(tryX)}|${Math.round(tryY)}`) && tries < 50) {
        tryX += 40;
        tryY += 40;
        tries++;
      }
      pos = { x: tryX, y: tryY };
      finalOccupiedPositions.add(`${Math.round(tryX)}|${Math.round(tryY)}`);
      return { ...node, position: pos };
    });

    // Ensure all node IDs are strings and use uid if present
    finalNodes = finalNodes.map(node => ({
      ...node,
      id: node.id !== undefined ? String(node.uid) : String(node.id)
    }));

    // Remove duplicate nodes by id
    const uniqueNodesMap = new Map();
    finalNodes.forEach(node => {
      if (!uniqueNodesMap.has(node.id)) {
        uniqueNodesMap.set(node.id, node);
      } else {
      }
    });
    finalNodes = Array.from(uniqueNodesMap.values());

    // --- CLEANUP: Remove userModifiedPositions for deleted nodes ---
    const validUids = new Set(finalNodes.map(n => n.id));
    for (const uid of Array.from(userModifiedPositions.current.keys())) {
      if (!validUids.has(uid)) {
        userModifiedPositions.current.delete(uid);
      }
    }

    // Rebuild nodeMap with final nodes to ensure consistency
    const finalNodeMap = new Map();
    finalNodes.forEach(node => {
      finalNodeMap.set(node.id, node);
    });

    // Create edges based on the incoming data structure
    let edges = [];
    
    // Create edges for concepts (AKO relationships)
    validConcepts.forEach(concept => {
      if (concept.ako) {
        concept.ako.forEach(parent => {
          const edgeColor = settings.monochromeView ? defaultColor : getNodeColorById(concept);
          const edge = {
            id: `ako-${String(getNodeKey(concept))}-${String(parent)}`,
            source: String(getNodeKey(concept)),
            target: String(parent),
            label: settings.showAkoLabels ? 'AKO' : '',
            type: 'default',
            animated: false,
            style: {
              stroke: edgeColor,
              strokeWidth: 1,
              strokeDasharray: '2,4'
            },
            labelStyle: { 
              fill: edgeColor, 
              fontWeight: 600, 
              fontSize: 13 
            },
            sourcePosition: 'bottom',
            targetPosition: 'top'
          };
          edges.push(edge);
        });
      }
    });
    
    // Create edges for individuals (ISA relationships)
    validIndividuals.forEach(individ => {
      if (individ.isa) {
        individ.isa.forEach(isa => {
          const edgeColor = settings.monochromeView ? defaultColor : 'rgb(112, 128, 242)';
          const edge = {
            id: `isa-${String(getNodeKey(individ))}-${String(isa)}`,
            source: String(getNodeKey(individ)),
            target: String(isa),
            label: settings.showIsaLabels ? 'ISA' : '',
            type: 'default',
            animated: false,
            style: {
              stroke: edgeColor,
              strokeWidth: 1,
              strokeDasharray: '8,2'
            },
            labelStyle: { 
              fill: settings.monochromeView ? defaultColor : getNodeColorById(individ), 
              fontWeight: 600, 
              fontSize: 13 
            },
            sourcePosition: 'top',
            targetPosition: 'bottom'
          };
          edges.push(edge);
        });
      }
      
      // Create edges for individuals (OBJECT relationships)
      if (individ.triplets) {
        Object.entries(individ.triplets).forEach(([k, v]) => {
          if (v && v.type === 'OBJECT' && v.value) {
            // Only create the edge if a node with key === v.value exists
            const targetNode = finalNodes.find(n => String(getNodeKey(n)) === String(v.value));
            if (targetNode) {
              const edgeColor = settings.monochromeView ? defaultColor : getNodeColorById(individ);
              const edge = {
                id: `obj-${String(getNodeKey(individ))}-${String(getNodeKey(targetNode))}`,
                source: String(getNodeKey(individ)),
                target: String(getNodeKey(targetNode)),
                label: k,
                type: 'default',
                animated: false,
                style: {
                  stroke: edgeColor,
                  strokeWidth: 2
                },
                labelStyle: { 
                  fill: edgeColor, 
                  fontWeight: 600, 
                  fontSize: 13 
                }
              };
              edges.push(edge);
            }
          }
        });
      }
    });

    // Ensure all edge IDs, sources, and targets are strings
    edges = edges.map(edge => ({
      ...edge,
      id: String(edge.id),
      source: String(edge.source),
      target: String(edge.target)
    }));

    // Remove duplicate edges by id
    const uniqueEdgesMap = new Map();
    edges.forEach(edge => {
      if (!uniqueEdgesMap.has(edge.id)) {
        uniqueEdgesMap.set(edge.id, edge);
      }
    });
    edges = Array.from(uniqueEdgesMap.values());

    // Ensure all referenced nodes by edges are present, add placeholders if missing
    const nodeIdsSet = new Set(finalNodes.map(n => String(n.id)));
    const referencedNodeIds = new Set();
    edges.forEach(edge => {
      referencedNodeIds.add(String(edge.source));
      referencedNodeIds.add(String(edge.target));
    });
    referencedNodeIds.forEach(refId => {
      if (!nodeIdsSet.has(refId)) {
        // Add a placeholder node
        const placeholderNode = {
          id: String(refId),
          type: 'concept', // or 'individ', but 'concept' is safe fallback
          position: { x: 0, y: 0 },
          data: { title: `Missing: ${refId}`, color: '#888' },
          style: { zIndex: 0, opacity: 0.5, border: '1px dashed #888' },
          isPlaceholder: true
        };
        finalNodes.push(placeholderNode);
        nodeIdsSet.add(refId);
      }
    });

    // --- Placeholders: move 'Missing' nodes closer to their references ---
    // Build a map of node positions for lookup
    const nodePosMap = {};
    finalNodes.forEach(n => { nodePosMap[n.id] = n.position; });
    // For stacking missing nodes at same spot
    const missingPosCount = {};
    // Find all placeholder nodes
    const placeholderNodes = finalNodes.filter(n => n.isPlaceholder);
    placeholderNodes.forEach((missingNode, idx) => {
      // Check if user has manually positioned this missing node
      const userPos = userModifiedPositions.current.get(missingNode.id);
      if (userPos) {
        // Keep user-modified position
        missingNode.position = userPos;
        nodePosMap[missingNode.id] = userPos;
        return;
      }

      // Find all edges where this node is source or target
      const relatedEdges = edges.filter(e => e.source === missingNode.id || e.target === missingNode.id);
      if (relatedEdges.length === 0) {
        // If no edges, place at bottom center of graph
        const nonPlaceholderNodes = finalNodes.filter(n => !n.isPlaceholder);
        if (nonPlaceholderNodes.length === 0) {
          // If no non-placeholder nodes, place at a default position
          missingNode.position = { x: 400, y: 600 };
        } else {
          const graphBottom = Math.max(...nonPlaceholderNodes.map(n => n.position.y)) + 100;
          const graphCenter = nonPlaceholderNodes.reduce((sum, n) => sum + n.position.x, 0) / nonPlaceholderNodes.length;
          missingNode.position = { x: graphCenter, y: graphBottom };
        }
        nodePosMap[missingNode.id] = missingNode.position;
        return;
      }

      // Find the closest connected node
      let closestNode = null;
      let minDistance = Infinity;
      relatedEdges.forEach(edge => {
        const otherId = edge.source === missingNode.id ? edge.target : edge.source;
        if (nodePosMap[otherId]) {
          const distance = Math.sqrt(
            Math.pow(nodePosMap[otherId].x - missingNode.position.x, 2) + 
            Math.pow(nodePosMap[otherId].y - missingNode.position.y, 2)
          );
          if (distance < minDistance) {
            minDistance = distance;
            closestNode = { id: otherId, position: nodePosMap[otherId] };
          }
        }
      });

      if (closestNode) {
        // Place missing node 100px below the closest node
        const newX = closestNode.position.x;
        const newY = closestNode.position.y + 100;
        
        // Check if position is already occupied and adjust if needed
        const posKey = `${Math.round(newX)}|${Math.round(newY)}`;
        missingPosCount[posKey] = (missingPosCount[posKey] || 0) + 1;
        const stackOffset = (missingPosCount[posKey] - 1) * 30;
        
        missingNode.position = { x: newX, y: newY + stackOffset };
        nodePosMap[missingNode.id] = missingNode.position;
      } else {
        // Fallback: place at bottom center
        const nonPlaceholderNodes = finalNodes.filter(n => !n.isPlaceholder);
        if (nonPlaceholderNodes.length === 0) {
          // If no non-placeholder nodes, place at a default position
          missingNode.position = { x: 400, y: 600 };
        } else {
          const graphBottom = Math.max(...nonPlaceholderNodes.map(n => n.position.y)) + 100;
          const graphCenter = nonPlaceholderNodes.reduce((sum, n) => sum + n.position.x, 0) / nonPlaceholderNodes.length;
          missingNode.position = { x: graphCenter, y: graphBottom };
        }
        nodePosMap[missingNode.id] = missingNode.position;
      }
    });

    // Final validation: log all node IDs and all edge source/target IDs
    const allNodeIds = finalNodes.map(n => n.id);
    const allEdgeSources = edges.map(e => e.source);
    const allEdgeTargets = edges.map(e => e.target);

    // Update previous positions for next render - only for nodes that don't have user-modified positions
    const finalPositions = new Map();
    finalNodes.forEach(node => {
      // Only store position if user hasn't modified it
      if (!userModifiedPositions.current.has(node.id)) {
        finalPositions.set(node.id, { x: node.position.x, y: node.position.y });
      }
    });
    previousPositions.current = finalPositions;
    
    // Only set elements and mark as ready if we have valid data
    if (finalNodes.length > 0 || edges.length > 0) {
      setElements({ nodes: finalNodes, edges: edges });
      setIsGraphReady(true);
    } else if (graph && (graph.concepts || graph.individuals)) {
      // Even if no nodes/edges, mark as ready if we have valid graph data
      setElements({ nodes: [], edges: [] });
      setIsGraphReady(true);
    } else {
      setIsGraphReady(false);
    }
  }, [graph, settings]);

  useEffect(() => {
    if (elements.nodes.length === 0) return;
    setElements(els => ({
      ...els,
      nodes: els.nodes.map(n => ({
        ...n,
        data: {
          ...n.data,
          selected: selectedNodeIds.includes(getNodeKey(n)),
          deletionMode: deletionMode,
        }
      })),
    }));
  }, [selectedNodeIds, deletionMode]);

  // Helper: toggle node selection
  const toggleSelectNode = useCallback((nodeId) => {
    setSelectedNodeIds(prev =>
      prev.includes(nodeId) ? prev.filter(id => id !== nodeId) : [...prev, nodeId]
    );
  }, []);

  // Helper: clear selection
  const clearSelectedNodes = useCallback(() => setSelectedNodeIds([]), []);

  // --- NodeTypes with selection/deletion props ---
  const nodeTypes = useMemo(() => ({
    concept: ConceptNode,
    individ: IndividNode
  }), []);

  // --- Delete selected nodes ---
  const handleDeleteSelectedNodes = async () => {
    setIsDeleting(true);
    try {
      // Store the nodes to be deleted for immediate removal from UI
      const nodesToDelete = selectedNodeIds.map(nodeId => {
        const node = elements.nodes.find(n => String(getNodeKey(n)) === String(nodeId));
        return {
          id: nodeId,
          rawId: node && node.rawId ? node.rawId : nodeId,
          type: node ? node.type : 'unknown'
        };
      });

      // Delete nodes from backend and track successful deletions
      const successfullyDeletedIds = [];
      for (let i = 0; i < nodesToDelete.length; i++) {
        const nodeToDelete = nodesToDelete[i];
        try {
          const response = await fetch('/rest/delete_node', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: nodeToDelete.rawId }),
          });
          
          if (response.ok) {
            successfullyDeletedIds.push(nodeToDelete.id);
          } else {
            console.error(`Failed to delete node ${nodeToDelete.rawId}:`, response.status, response.statusText);
          }
          
          // Add delay between requests to ensure proper processing
          if (i < nodesToDelete.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 200)); // 200ms delay between requests
          }
        } catch (error) {
          console.error(`Error deleting node ${nodeToDelete.rawId}:`, error);
        }
      }

      // Only remove successfully deleted nodes from local state
      if (successfullyDeletedIds.length > 0) {
        const updatedNodes = elements.nodes.filter(node => 
          !successfullyDeletedIds.includes(String(getNodeKey(node)))
        );
        
        // Remove edges connected to successfully deleted nodes
        const updatedEdges = elements.edges.filter(edge => 
          !successfullyDeletedIds.includes(String(edge.source)) && 
          !successfullyDeletedIds.includes(String(edge.target))
        );

        // Clean up userModifiedPositions for successfully deleted nodes
        successfullyDeletedIds.forEach(nodeId => {
          userModifiedPositions.current.delete(nodeId);
        });

        // Track deleted IDs to prevent them from reappearing in server responses
        const deletedRawIds = nodesToDelete
          .filter(node => successfullyDeletedIds.includes(node.id))
          .map(node => node.rawId);
        if (trackDeletedIds && deletedRawIds.length > 0) {
          trackDeletedIds(deletedRawIds);
        }

        // Update local state immediately
        setElements({ nodes: updatedNodes, edges: updatedEdges });
      }

      // Clear selection for all attempted deletions
      clearSelectedNodes();
      
      // Update graph data from server to ensure consistency
      // Add a longer delay to ensure backend has processed all deletions
      setTimeout(() => {
        updateGraph();
      }, 1000); // Increased from 500ms to 1000ms
      
    } catch (e) {
      console.error('Error in handleDeleteSelectedNodes:', e);
      // If deletion failed, refresh the graph to show current state
      updateGraph();
    }
    setIsDeleting(false);
  };

  // Memoize the elements array to ensure React Flow gets stable references
  const elementsArray = useMemo(() => {
    const result = [...elements.nodes, ...elements.edges];
    return result;
  }, [elements.nodes, elements.edges]);

  // --- Dynamic nodeExtent calculation ---
  const nodeExtent = useMemo(() => {
    if (!elements.nodes || elements.nodes.length === 0) {
      return [[-1000, -1000], [2000, 2000]]; // fallback
    }
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    elements.nodes.forEach(node => {
      if (node.position) {
        if (node.position.x < minX) minX = node.position.x;
        if (node.position.y < minY) minY = node.position.y;
        if (node.position.x > maxX) maxX = node.position.x;
        if (node.position.y > maxY) maxY = node.position.y;
      }
    });
    // Add margin
    const margin = 300;
    minX -= margin;
    minY -= margin;
    maxX += margin;
    maxY += margin;
    // Fallback if all nodes at 0,0
    if (!isFinite(minX) || !isFinite(minY) || !isFinite(maxX) || !isFinite(maxY)) {
      return [[-1000, -1000], [2000, 2000]];
    }
    return [[minX, minY], [maxX, maxY]];
  }, [elements.nodes]);

  // Track previous node IDs to detect new nodes
  const prevNodeIdsRef = useRef([]);

  useEffect(() => {
    const currentNodeIds = elements.nodes.map(n => n.id);
    const prevNodeIds = prevNodeIdsRef.current;
    // If there are new nodes (ids in current but not in prev), turn off deletionMode
    const newNodeAdded = currentNodeIds.some(id => !prevNodeIds.includes(id));
    if (newNodeAdded && deletionMode) {
      setDeletionMode(false);
    }
    prevNodeIdsRef.current = currentNodeIds;
  }, [elements.nodes, deletionMode]);

  return (
    <React.Fragment>
      {isGraphReady ? (
        <React.Fragment>
          {/* Delete button in bottom-right if nodes selected */}
          <Box sx={{
            position: 'absolute',
            bottom: 36,
            right: 36,
            gap: 3,
            zIndex: 2000,
            display: 'flex',
            alignItems: 'center',
          }}>
            {selectedNodeIds.length > 0 && (
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}>
                <IconButton
                  alt="Graph edit mode"
                  onClick={isDeleting ? undefined : handleDeleteSelectedNodes}
                  sx={{
                    bgcolor: isDeleting ? 'rgba(255,255,255,0.2)' : 'rgba(25, 23, 23, 0.9)',
                    color: 'white',
                    borderRadius: '50%',
                    width: 40,
                    height: 40,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
                    '&:hover': {
                      bgcolor: isDeleting ? 'rgba(255,255,255,0.2)' : 'rgba(0, 0, 0, 0.8)',
                    },
                    fontSize: 28,
                  }}
                  disabled={isDeleting}
                >
                  <DeleteForeverOutlinedIcon style={{ color: '#d32f2f', height: '24px', width: '24px' }}/>
                </IconButton>
                <IconButton
                  onClick={clearSelectedNodes}
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.12)',
                    color: 'white',
                    borderRadius: '50%',
                    width: 36,
                    height: 36,
                    '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.22)',
                    },
                    fontSize: 18,
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="9" cy="9" r="9" fill="#888" />
                    <path d="M6 6l6 6M12 6l-6 6" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                </IconButton>
              </Box>
            )}
            <IconButton
              size="small"
              onClick={() => setDeletionMode((v) => !v)}
              sx={{
                bgcolor: deletionMode ? 'rgba(211,47,47,0.12)' : 'rgba(255,255,255,0.10)',
                outline: deletionMode ? '2px solid #d32f2f' : '2px solid transparent',
                color: deletionMode ? '#d32f2f' : '#fff',
                width: 32,
                height: 32,
                boxShadow: '0 1px 4px rgba(0,0,0,0.10)',
                transition: 'all 0.15s',
                '&:hover': {
                  bgcolor: 'rgba(211,47,47,0.18)',
                },
              }}
            >
              <ModeOutlinedIcon style={{ fontSize: 18 }} />
            </IconButton>
          </Box>

          <StyledReactFlowComponent
            ref={wrapperRef}
            elements={elementsArray}
            nodeTypes={nodeTypes}
            nodesDraggable
            nodesConnectable
            elementsSelectable
            zoomOnScroll
            panOnScroll={false}
            minZoom={0.1}
            maxZoom={2}
            defaultZoom={defaultZoom}
            onLoad={onLoad}
            onMoveEnd={onMoveEnd}
            zoomOnPinch
            snapToGrid
            snapGrid={[10, 10]}
            zoomOnDoubleClick={false}
            preventScrolling={false}
            selectNodesOnDrag={false}
            onNodeDragStop={onNodeDragStop}
            nodeExtent={nodeExtent}
            showinteractive={"false"}
          >
            <Background color="#2f4361" gap={16} />
            <Controls showInteractive={false}>
              <ControlButton onClick={() => {
                if (!document.fullscreenElement) {
                  const element = wrapperRef.current;
                  if (element && element.requestFullscreen) {
                    element.requestFullscreen().then(() => setIsFullScreen(true));
                  }
                } else {
                  if (document.exitFullscreen) {
                    document.exitFullscreen().then(() => setIsFullScreen(false));
                  }
                }
              }} title="Toggle fullscreen">
                {isFullScreen ? (
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#000000" strokeWidth="2">
                    <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3" />
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#000000" strokeWidth="2">
                    <path d="M3 8V3h5M3 16v5h5h5m13-5v5h-5m5-13V3h-5" />
                  </svg>
                )}
              </ControlButton>
            </Controls>
          </StyledReactFlowComponent>
        </React.Fragment>
      ) : (
        <Box sx={{ 
          display: 'flex', 
          height: '100%', 
          alignItems: 'center', 
          justifyContent: 'center',
          color: 'rgba(255, 255, 255, 0.7)',
          fontSize: '14px'
        }}>
          Preparing graph...
        </Box>
      )}
    </React.Fragment>
  );
});

OntologyGraph.displayName = 'OntologyGraph';

export default OntologyGraph; 